import { Case } from "@/lib/useRealtimeCases";
import { statusColors } from "@/lib/statusColors";
import { useSelectedCenters } from "@/lib/SelectedCentersContext";
import { formatParisDate } from "@/lib/parisDateTime";
import { reminderProgress, type ReminderCycle } from "@/lib/reminderProgress";
import { ReminderProgressBar } from "./ReminderProgressBar";
import type { CaseTask } from "@/lib/caseTasks";
import { flameLabel } from "@/lib/kanbanTasks";
import { TaskFlame } from "@/components/tasks/TaskFlame";

interface CaseCardProps {
  case: Case;
  onClick: () => void;
  hasUnread: boolean; // ← Reçu en prop depuis KanbanPage (évite 1 channel par case)
  hasRecentSignature: boolean; // ← Nouveau: pour la pastille signature récente
  reminderCycle?: ReminderCycle; // dernier cycle de relance devis du dossier
  /** Mes tâches ouvertes sur ce dossier → flamme en haut à gauche (personnelle). */
  myTasks?: CaseTask[];
  /** Clic droit (manager seulement ; sinon non fourni → menu natif du navigateur). */
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

export function CaseCard({ case: caseData, onClick, hasUnread, hasRecentSignature, reminderCycle, myTasks, onContextMenu }: CaseCardProps) {
  const { selectedCenters } = useSelectedCenters();

  // Vérifier si le centre du dossier est dans les centres sélectionnés
  const shouldShowBadge = hasUnread && (
    selectedCenters.length === 0 || 
    selectedCenters.includes(caseData.center || "")
  );

  const shouldShowSignatureBadge = hasRecentSignature && (
    selectedCenters.length === 0 || 
    selectedCenters.includes(caseData.center || "")
  );

  const clientName = [caseData.client_first_name, caseData.client_last_name]
    .filter(Boolean)
    .join(" ") || "Client non renseigné";

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    try {
      return formatParisDate(dateStr, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return null;
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTimeSinceStatus = (
    status: string,
    createdAt: string,
    lastQuoteSentAt: string | null
  ): string | null => {
    if (status !== "Non traité" && status !== "Devis envoyé") {
      return null;
    }

    const now = new Date();
    let referenceDate: Date;

    if (status === "Non traité") {
      referenceDate = new Date(createdAt);
    } else {
      referenceDate = lastQuoteSentAt ? new Date(lastQuoteSentAt) : new Date(createdAt);
    }

    const diffMs = now.getTime() - referenceDate.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) return `Depuis ${diffDays}j`;
    if (diffHours > 0) return `Depuis ${diffHours}h`;
    return "Depuis <1h";
  };

  // Prioritaire = uniquement la priorité posée par le manager (plus de pastille automatique au montant, Paul 04/11).
  const isPriority = !!caseData.is_priority;
  const flameText = myTasks && myTasks.length > 0 ? flameLabel(myTasks) : null;
  const reminders = reminderProgress(caseData, reminderCycle);

  const timeSinceStatus = getTimeSinceStatus(
    caseData.status,
    caseData.created_at,
    caseData.last_quote_sent_at
  );

  return (
    <div
      onClick={onClick}
      onContextMenu={onContextMenu}
      data-case-id={caseData.id}
      className={`bg-white rounded-lg border shadow-sm p-3 cursor-pointer hover:shadow-md transition-shadow relative ${
        isPriority ? "border-red-300 ring-2 ring-red-200" : "border-gray-200"
      }`}
      style={{
        borderLeftWidth: "4px",
        borderLeftColor: statusColors[caseData.status] || "#E5E7EB",
      }}
    >
      {/* Flamme : j'ai une tâche ouverte sur ce dossier (visible par moi seulement) */}
      {flameText && (
        <div
          className="absolute -top-2 -left-2 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md ring-1 ring-orange-200 z-10"
          role="img"
          aria-label={flameText}
          title={flameText}
        >
          <TaskFlame className="h-4 w-4" />
        </div>
      )}

      {/* Badge notification messages non lus (filtré par centres sélectionnés) */}
      {shouldShowBadge && (
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-lg z-10">
          <span className="text-white text-xs font-bold">!</span>
        </div>
      )}

      {/* Badge signature récente (décalé si badge message présent) */}
      {shouldShowSignatureBadge && (
        <div 
          className="absolute -top-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-lg z-10"
          style={{ right: shouldShowBadge ? '20px' : '-6px' }}
        >
          <span className="text-white text-xs font-bold">✓</span>
        </div>
      )}

      {/* Pastille « Prioritaire » : posée par le manager (clic droit) */}
      {isPriority && (
        <div
          className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded mb-1.5 bg-red-600 text-white"
          title="Marqué prioritaire par le manager"
        >
          Prioritaire
        </div>
      )}

      {/* Entreprise (en premier et en gras) */}
      {caseData.company_name && (
        <div className="font-bold text-sm text-gray-900 mb-1 truncate">
          {caseData.company_name}
        </div>
      )}

      {/* Client */}
      <div className="text-xs text-gray-600 mb-2 truncate">
        {clientName}
      </div>

      {/* Activité + Centre */}
      <div className="flex gap-2 mb-2 flex-wrap">
        {caseData.activity_type && (
          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
            {caseData.activity_type}
          </span>
        )}
        {caseData.center && (
          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
            {caseData.center}
          </span>
        )}
        {/* Parc sport en 2e choix : un commercial Smile Event répartit la demande */}
        {caseData.secondary_center && /climb|vertical|padel/i.test(caseData.secondary_center) && (
          <span className="text-xs bg-violet-50 text-violet-700 px-2 py-0.5 rounded" title="Deuxième parc demandé : à répartir">
            + {caseData.secondary_center}
          </span>
        )}
      </div>

      {/* Date événement */}
      {caseData.event_date && (
        <div className="text-xs text-gray-500 mb-1">
          📅 {formatDate(caseData.event_date)}
        </div>
      )}

      {/* PAX */}
      {caseData.pax && (
        <div className="text-xs text-gray-500 mb-1">
          👥 {caseData.pax} personnes
        </div>
      )}

      {/* Budget */}
      {caseData.budget && (
        <div className="text-xs font-medium text-gray-700">
          💰 {formatCurrency(caseData.budget)}
        </div>
      )}

      {/* Gros devis (≥ 1 500 € TTC) : un appel aide à conclure */}
      {reminders?.callRecommended && (
        <div className="mt-2 inline-flex items-center text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-orange-50 text-orange-800" title="Devis de 1 500 € TTC ou plus : un appel est recommandé pendant les relances">
          Appel recommandé · ≥ 1 500 € TTC
        </div>
      )}

      {/* Relances devis : barre de progression */}
      {reminders && <ReminderProgressBar view={reminders} />}

      {/* Bas de carte: pastille type client + compteur ancienneté */}
      {(caseData.client_type || timeSinceStatus) && (
        <div
          className={`mt-2 pt-2 border-t border-gray-100 flex items-center gap-2 ${
            caseData.client_type ? "justify-between" : "justify-end"
          }`}
        >
          {caseData.client_type && (
            <span className="inline-flex items-center text-xs font-medium px-2 py-1 rounded bg-slate-100 text-slate-700">
              {caseData.client_type}
            </span>
          )}

          {timeSinceStatus && (
            <span className="text-xs text-gray-500">
              {timeSinceStatus}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
